// src/lib/missions/claim.ts
/**
 * La riscossione, come REGOLA: dall'esito sul filo alla faccia che la riga mostra.
 *
 * Sta qui e non nel componente per la stessa ragione per cui ci sta `model.ts` — e in questo caso
 * la ragione e' piu' forte, non piu' debole: questo e' il primo percorso della piattaforma in cui
 * un gesto dell'utente muove denaro, e i suoi otto esiti non sono esercitabili a mano. Un `503` con
 * la tesoreria vuota, un `409 already_claimed` da doppio invio, una connessione caduta a meta'
 * chiamata: nessuno di questi si riproduce aprendo l'app. Se la mappa e' sbagliata, il modo in cui
 * lo si scopre e' un utente vero che vede la parola sbagliata sul proprio premio.
 *
 * 🔴 I TRE VINCOLI CHE QUESTA MAPPA ESISTE PER FAR RISPETTARE (ADR-0073 + il contratto in
 * `Quest/docs/INTER_SERVICE_COMMUNICATION.md`; sorgente letta a `f64873a`):
 *
 *  1. **`ok: true` significa DOVUTO, non PAGATO.** Con `state: 'pending'` il trasferimento a QBank
 *     non e' andato a buon fine ed e' in carico al worker di re-drive — ma `claimedAt` viene
 *     stampigliato lo stesso (`claim.service.ts`: l'istanza passa a `claimed` qualunque sia l'esito
 *     del payout, purche' la chiamata a QBank sia avvenuta). Lo stato di consegna vive su `Payout`
 *     e NON e' esposto ad alcuna rotta giocatore: una copy «premio ricevuto» sarebbe una promessa
 *     che nulla, mai, potra' smentire. ⇒ `delivery` distingue i tre casi, e nessuno dei tre porta
 *     una stringa che parli di accredito.
 *  2. **`503` non e' un errore dell'utente ne' un rifiuto.** `budget_blocked` (tesoreria vuota) e
 *     `fulfiller_unavailable` (cosmetici giu') lasciano l'istanza `completed` con `claimedAt: null`
 *     — cioe' ANCORA RISCUOTIBILE. Il premio resta dovuto e il gesto si ripete piu' tardi. ⇒ non
 *     `stale`, ma `retryable`: il bottone resta.
 *  3. **`409 already_claimed` e' un successo, non un fallimento.** E' cio' che risponde la seconda
 *     pressione o un replay: la riscossione ERA gia' passata. ⇒ `claimed`, non `stale`, e nessuna
 *     schermata d'errore.
 */

/**
 * Cosa e' successo, nei soli termini che cambiano il comportamento della riga.
 *
 * Quattro rami perche' le cose che la riga puo' fare sono quattro, non otto: mostrarsi riscossa
 * (`claimed`), prendere atto che la nostra copia era vecchia (`stale`), lasciare il bottone perche'
 * il premio e' ancora dovuto (`retryable`), o mandare l'utente a rifare l'accesso (`reauth`).
 * Raggruppare per COMPORTAMENTO e non per codice HTTP e' cio' che impedisce a un esito nuovo di
 * finire nel ramo sbagliato: chi aggiunge un caso deve scegliere che cosa fa la riga, non che
 * numero e' arrivato.
 */
export type ClaimOutcome =
  | {
      kind: 'claimed';
      /**
       * `committed` = QBank ha confermato il trasferimento · `pending` = e' in carico al re-drive ·
       * `already` = era gia' stata riscossa (409), e non sappiamo quale dei due.
       *
       * ⚠️ `committed` e `already` mostrano la STESSA stringa («Riscossa»), e non e' una svista da
       * semplificare fondendoli: sono due fatti diversi — noi abbiamo appena mosso il denaro, oppure
       * non abbiamo fatto nulla perche' era gia' fatto — e chi legge questo codice ha bisogno di
       * saperlo. A coincidere e' solo la copy, perche' «Riscossa» e' l'unica cosa vera in entrambi.
       */
      delivery: 'committed' | 'pending' | 'already';
    }
  | {
      /** La nostra copia della lista era vecchia: non c'e' nulla da riscuotere qui. Il bottone
       *  sparisce, la lista si ricarica, e la riga si sistema da sola col dato del server. */
      kind: 'stale';
      reason: 'notCompleted' | 'expired' | 'notFound';
    }
  | {
      /** Il premio e' ANCORA DOVUTO: il gesto si ripete. Il bottone resta. */
      kind: 'retryable';
      reason: 'unavailable' | 'unknown' | 'network';
    }
  | {
      /**
       * La riscossione non c'e' in questo ambiente: sulla via Limulo, un rifiuto senza motivo
       * nominato (`CLAIM_NOT_SERVED`: la rotta non servita, l'interruttore spento) o `disabled`.
       *
       * 🔴 Ha un ramo SUO, e non `reauth`, perche' la copy di `reauth` accusa la SESSIONE di un
       * difetto di CONFIGURAZIONE: chi la legge rifa' l'accesso, l'accesso non c'entra, e il
       * secondo tentativo fallisce identico — un ciclo da cui l'utente non esce (misurato su
       * `stg-tma` da `quest-orc`, 2026-09-20 11:0xZ: lista `limulo`, claim `quest`). E non e'
       * `retryable`: ripetere non puo' funzionare finche' la configurazione resta questa.
       */
      kind: 'unsupported';
    }
  | {
      /**
       * Il token non porta `quest:missions:claim`.
       *
       * 🔴 E' il modo in cui questo rilascio fallisce per PRIMO, e per tutti: lo scope e' nuovo,
       * e un refresh token emesso prima non lo acquista — serve un nuovo accesso interattivo. Senza
       * un ramo suo, questo caso cadrebbe in `retryable` e direbbe «riprova piu' tardi» a un utente
       * per cui riprovare non funzionera' mai.
       */
      kind: 'reauth';
    };

/** Una stringa da un corpo di cui non sappiamo nulla, o `null`. Nessun cast, nessuna fiducia. */
function field(body: unknown, key: string): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const value = (body as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : null;
}

function okIsTrue(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as Record<string, unknown>).ok === true;
}

/**
 * Lo `status` che `questApi` da' a un rifiuto del claim sulla via LIMULO che NON porta un motivo
 * nominato (`details.claimReason`): la rotta non ancora servita (404 senza motivo), l'interruttore
 * `enableQuestClaims` spento (403 `E_QLASH_ENDPOINT_DISABLED`, qlash-limulo#155), nessun provider
 * (424). ⇒ `unsupported`: la riscossione in questo ambiente non c'e' ancora, e la frase lo dice.
 * ⚠️ NON «missione non trovata»: un 404 della ROTTA direbbe al giocatore che la sua missione non c'e'.
 *
 * Fino al 2026-09-27 (I-223, L-2317) qui c'era `CLAIM_BACKEND_MISMATCH`, il rifiuto locale di un
 * claim che andava sempre a Quest mentre la lista veniva da Limulo. Da quando il claim segue la
 * lista (`questApi`), quel caso non esiste piu'; la faccia che lo raccontava e' la stessa.
 *
 * Vive qui, e non in `questApi`, per la stessa ragione per cui ci vive la mappa: chi emette il
 * verdetto e chi lo legge devono nominare la stessa costante, o il giorno che una delle due
 * cambia l'altra ricade nel default senza che niente diventi rosso.
 */
export const CLAIM_NOT_SERVED = 'CLAIM_NOT_SERVED';

/**
 * I motivi che il BFF di Limulo mette in `details.claimReason` (qlash-limulo#155, `quest-claim.js`
 * `CLAIM_STATUSES`; disegno: KB `services/quest/riscossione-su-limulo.md` §8). ⚠️ Copia
 * dell'allowlist del server: se la cambia, si rilegge QUESTA lista contro la sua.
 */
export const LIMULO_CLAIM_REASONS = [
  'not_found',
  'already_claimed',
  'not_completed',
  'owed',
  'expired',
  'disabled',
] as const;
export type LimuloClaimReason = (typeof LIMULO_CLAIM_REASONS)[number];

/** Il motivo nominato di un rifiuto Limulo (`data.details.claimReason`), o `null`. */
export function limuloClaimReasonOf(body: unknown): LimuloClaimReason | null {
  const details =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>).details : undefined;
  const raw =
    typeof details === 'object' && details !== null
      ? (details as Record<string, unknown>).claimReason
      : undefined;
  return LIMULO_CLAIM_REASONS.find((r) => r === raw) ?? null;
}

/**
 * L'errore di un claim sulla via LIMULO, nella forma che `claimOutcome` legge. Tre casi:
 *  · un motivo NOMINATO passa com'e' (lo status e' quello del contratto, il motivo lo legge la mappa);
 *  · rete caduta o timeout, 401 (la sessione), 5xx e 429 (un guasto o un freno, il premio resta
 *    dovuto) passano com'e': non sono configurazione, e la mappa li racconta come sulla via Quest.
 *    Anche un 502/504 con corpo HTML, che arriva come `PARSING_ERROR` con `originalStatus` ≥ 500:
 *    durante un deploy di Limulo direbbe «su staging non si riscuote» per un premio dovuto
 *    (revisione di #462, punto 6);
 *  · tutto il resto — un 404 della rotta, il 403 dell'interruttore spento, il 424 — diventa
 *    `CLAIM_NOT_SERVED`, cioe' `unsupported`.
 * Pura: la usa `questApi.claimMission`, la provano i banchi senza rete.
 */
export function limuloClaimError(error: WireErrorLike): WireErrorLike {
  if (limuloClaimReasonOf(error.data) !== null) return error;
  const status = error.status;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR' || status === 401) return error;
  if (typeof status === 'number' && (status >= 500 || status === 429)) return error;
  const originale = (error as { originalStatus?: unknown }).originalStatus;
  if (status === 'PARSING_ERROR' && typeof originale === 'number' && originale >= 500) return error;
  return { status: CLAIM_NOT_SERVED, data: { originalStatus: status ?? null } };
}

/**
 * La mappa. `status` e' il codice HTTP oppure una delle stringhe che RTK Query usa quando una
 * risposta non c'e' stata affatto (`FETCH_ERROR`, `TIMEOUT_ERROR`).
 *
 * Totale per costruzione: ogni ingresso che non riconosce cade in `retryable/unknown`, che e' il
 * ramo giusto per l'ignoto — dice «riprova, il premio resta tuo», che su questo percorso e' vero
 * qualunque cosa sia successa. Il claim e' idempotente lato server (indice unico su `Payout.
 * instanceId`), quindi una ripetizione o ripaga nulla e torna `already_claimed`, o riesce: in
 * nessun caso paga due volte. E' cio' che rende sicuro il default piu' generoso.
 */
export function claimOutcome(status: number | string, body: unknown): ClaimOutcome {
  if (status === 200) {
    // Difesa e non paranoia: la rotta manda `ok:false` solo con uno status di errore, ma fra noi e
    // Quest ci sono Cloudflare e un proxy Vite. Un 200 il cui corpo non dice `ok:true` non e' una
    // riscossione, e trattarlo come tale sarebbe l'unico modo di mostrare «Riscossa» senza esserlo.
    if (!okIsTrue(body)) return { kind: 'retryable', reason: 'unknown' };
    // Solo `pending` merita la parola in piu'. Ogni altro valore (compreso uno che non conosciamo)
    // ricade su `committed`, perche' la sua copy — «Riscossa» — e' l'unica cosa che `ok:true`
    // garantisce da sola, mentre «premio in arrivo» direbbe in piu' che NON e' arrivato.
    return { kind: 'claimed', delivery: field(body, 'state') === 'pending' ? 'pending' : 'committed' };
  }
  if (status === 401 || status === 403) return { kind: 'reauth' };
  // La riscossione non servita in questo ambiente (via Limulo, rifiuto senza motivo nominato:
  // `limuloClaimError`). Senza questa riga cadrebbe nel default in fondo — `retryable/unknown` — e
  // il bottone direbbe RIPROVA a un utente per cui riprovare non puo' funzionare finche' la
  // rotta non c'e'. ⚠️ E' una STRINGA e non uno status finto, di proposito: un 404 finto sarebbe
  // indistinguibile da un rifiuto vero del server, e il giorno che si legge un registro (o si
  // scrive una metrica) i due casi vanno contati separatamente.
  if (status === CLAIM_NOT_SERVED) return { kind: 'unsupported' };
  // Il motivo: `reason` sulla via Quest, `details.claimReason` sulla via Limulo (i due non
  // convivono nello stesso corpo). Stessi nomi per gli stessi fatti.
  const motivo = field(body, 'reason') ?? limuloClaimReasonOf(body);
  // 404 e' anche l'istanza di un ALTRO utente, indistinguibile di proposito (`claim.routes.ts`).
  // Per chi guarda e' comunque un fatto solo: questa riga non c'e' piu' come credevamo.
  if (status === 404) return { kind: 'stale', reason: 'notFound' };
  if (status === 409) {
    // Il ramo che NON deve mai diventare un errore.
    if (motivo === 'already_claimed') return { kind: 'claimed', delivery: 'already' };
    // Via Limulo: il denaro e' DOVUTO e non ancora pagato (la riga di pagamento esiste, il ledger
    // non ha confermato). E' il 503 della via Quest portato a 409 dal BFF, che promuove solo i 4xx:
    // il premio resta tuo, il gesto si ripete. ⛔ Non «non completata», che sarebbe falso.
    if (motivo === 'owed') return { kind: 'retryable', reason: 'unavailable' };
    // Via Limulo: la riscossione e' spenta in questo ambiente (interruttore o configurazione).
    if (motivo === 'disabled') return { kind: 'unsupported' };
    // Un 409 con un `reason` che non conosciamo cade qui e non fra le riscosse: sbagliare per
    // difetto costa una frase imprecisa, sbagliare per eccesso mostrerebbe come riscossa una riga
    // che non lo e'. E la lista si ricarica comunque su ogni 409, quindi l'errore si corregge solo.
    return { kind: 'stale', reason: 'notCompleted' };
  }
  if (status === 410) return { kind: 'stale', reason: 'expired' };
  // I due 503 (`budget_blocked`, `fulfiller_unavailable`) condividono la faccia perche' condividono
  // il fatto: il premio resta dovuto e si riprova. Distinguerli a schermo racconterebbe all'utente
  // un problema di piattaforma che non lo riguarda e non cambierebbe cio' che puo' fare.
  if (status === 503) return { kind: 'retryable', reason: 'unavailable' };
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return { kind: 'retryable', reason: 'network' };
  }
  return { kind: 'retryable', reason: 'unknown' };
}

/** La parte di `FetchBaseQueryError` che serve, dichiarata a mano: questo modulo non importa RTK,
 *  cosi' resta verificabile senza montare React ne' uno store. */
export interface WireErrorLike {
  status?: number | string;
  data?: unknown;
}

/** Lo stesso verdetto a partire dall'errore che RTK Query consegna. */
export function claimOutcomeFromError(error: unknown): ClaimOutcome {
  const wire = error as WireErrorLike | null | undefined;
  return claimOutcome(wire?.status ?? 'NO_STATUS', wire?.data);
}

/**
 * La chiave i18n della faccia. Sta qui, e non in una tabella dentro il componente, perche' e' il
 * punto in cui si verifica meccanicamente che NESSUN ESITO SIA MUTO: il verificatore percorre gli
 * esiti reali, chiede la chiave, e pretende che esista in entrambe le lingue.
 *
 * Lo `switch` e' esaustivo per il compilatore: aggiungere un ramo a `ClaimOutcome` senza dargli una
 * stringa non compila.
 */
export function claimOutcomeKey(outcome: ClaimOutcome): string {
  switch (outcome.kind) {
    case 'claimed':
      return outcome.delivery === 'pending' ? 'claim.result.pending' : 'claim.result.claimed';
    case 'stale':
      return `claim.result.${outcome.reason}`;
    case 'retryable':
      return `claim.result.${outcome.reason}`;
    case 'unsupported':
      return 'claim.result.unsupported';
    case 'reauth':
      return 'claim.result.reauth';
  }
}

/**
 * Vero se il gesto ha ancora senso ⇒ il bottone RESTA.
 *
 * E' la regola del vincolo 2 resa eseguibile: su `503` e su una rete caduta il premio e' ancora
 * dovuto, e togliere il bottone lascerebbe l'utente esattamente nella condizione che questo lavoro
 * nasce per chiudere — un'istruzione a compiere un gesto che non esiste da nessuna parte.
 * `reauth` sta qui per la stessa ragione: il premio e' li', manca solo un accesso.
 */
export function claimIsRepeatable(outcome: ClaimOutcome): boolean {
  // ⛔ `unsupported` NON sta qui: il gesto non puo' riuscire finche' la configurazione e' quella,
  // e un bottone che resta e' l'istruzione a ripetere un gesto impossibile.
  return outcome.kind === 'retryable' || outcome.kind === 'reauth';
}

/**
 * Vero se questo esito significa che il DENARO SI E' MOSSO (o e' in volo) ⇒ il saldo del wallet
 * puo' essere cambiato, e la sua cache — che vive in un altro client RTK — va invalidata.
 *
 * 🔴 SCRITTA DOPO IL PRIMO CLAIM VERO (2026-08-30, 11:19:14Z), che e' andato bene nel denaro e male
 * nella percezione: il saldo essenze e' passato da 2100 a 2101 e l'utente ha scritto *«fatto
 * reclaim, confermato, ma non vedo l'essenza fra le mie valute wallet»*. Il claim invalidava la
 * lista delle missioni e nient'altro. Il difetto non stava in nessuno dei due client: stava nel
 * fatto che il denaro attraversa il confine fra loro e l'invalidazione no.
 *
 * ⚠️ E' `kind === 'claimed'`, cioe' TUTTI E TRE i rami, e i due meno ovvi vanno letti:
 *
 *  - **`pending`**: il trasferimento e' in volo. Sembra inutile ricaricare — mostrerebbe il saldo
 *    vecchio — ma `pending` nasce da una risposta di QBank `retryable` (un timeout, per esempio),
 *    e uno stato NON e' la stessa cosa di un fallimento: il trasferimento potrebbe essere passato
 *    davvero dall'altra parte. Il refetch e' l'unica occasione di accorgersene. E se non e'
 *    passato, mostra il saldo vecchio — che in quel momento e' il saldo VERO, accanto a una riga
 *    che dice «Premio in arrivo». Nessuna delle due cose mente.
 *  - **`already`** (409 `already_claimed`): l'esito della SECONDA pressione dopo che la prima
 *    aveva mosso il denaro senza che noi ne vedessimo la risposta (rete caduta a meta' chiamata).
 *    E' il caso in cui il saldo e' cambiato e noi non l'abbiamo mai saputo, ed e' anche quello in
 *    cui l'utente ha premuto due volte e sta guardando piu' attentamente di chiunque altro. Non
 *    invalidare proprio qui sarebbe lasciare il difetto in piedi nel suo scenario peggiore.
 *
 * E ⇒ tutto il resto NO: su `503` la tesoreria non ha pagato nulla, su `stale` non c'era nulla da
 * pagare, su `reauth` la chiamata non e' mai arrivata a QBank. Invalidare li' sarebbe una richiesta
 * di rete a vuoto su una rotta che, nel caso del 503, e' gia' in difficolta'.
 */
export function claimMovedMoney(outcome: ClaimOutcome): boolean {
  return outcome.kind === 'claimed';
}
